import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as service from "./service";
import { PEV, ProgressErrorValue } from "../../../utils/progress-error-value";
import { INeoErrorInfo, NeoError } from "../../../utils/errors/neo-errors";
import { Nullable } from "../../../utils/nullable";
import { createAsyncThunkWithRejectValue } from "../../../common/redux/createAsyncThunkWithRejectValue";
import { ApplicationBasicInfo } from "../../applications/application-details/redux";

export interface IMyApplicationFilters {
  searchText: string;
  teamTypes: string[];
  countries: string[];
  businessFunctions: string[];
  statuses: string[];
  categories: string[];
  displayInactiveApplications: boolean;
}

interface IMyAppsAndServicesState {
  filteredMyApplications: ApplicationBasicInfo[];
  myApplicationsPEV: ProgressErrorValue<ApplicationBasicInfo[], Nullable<string>, Nullable<INeoErrorInfo>>;
  filters: IMyApplicationFilters;
}

const initialState: IMyAppsAndServicesState = {
  filteredMyApplications: [],
  myApplicationsPEV: PEV([]),
  filters: {
    searchText: "",
    teamTypes: [],
    countries: [],
    businessFunctions: [],
    statuses: [],
    categories: [],
    displayInactiveApplications: false,
  },
};

const fetchMyApplications = createAsyncThunkWithRejectValue<ApplicationBasicInfo[]>(
  "my-applications/get",
  service.getMyApplications,
);
const filterApplication = (
  applications: ApplicationBasicInfo[],
  selectedValues: string[],
  filterType: "teamType" | "country" | "businessFunction" | "status" | "categories",
) => {
  function isSelectedValuesNotEmpty() {
    return selectedValues.length > 0;
  }

  if (isSelectedValuesNotEmpty()) {
    if (filterType === "teamType") {
      return applications.filter((application) => selectedValues.includes(application.team.teamType));
    }
    if (filterType === "categories") {
      return applications.filter((application) => application.category.some((category) => selectedValues.includes(category)));
    }
    return applications.filter((application) => selectedValues.includes(application[filterType]));
  }
  return applications;
};
export const myAppsAndServicesPageSlice = createSlice({
  name: "myAppsAndServicesPage",
  initialState,
  reducers: {
    updateApplicationsPageState: (state: IMyAppsAndServicesState, action: PayloadAction<Partial<IMyAppsAndServicesState>>) => ({
      ...state,
      ...action.payload,
    }),
    applyFilters: (
      state: IMyAppsAndServicesState,
      action: PayloadAction<{
        filters: Partial<IMyApplicationFilters>;
        activeApplicationStatuses: Array<ApplicationBasicInfo["status"]>;
      }>,
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload.filters,
      };
      const searchTextInUpperCase = state.filters.searchText.toUpperCase().trim();
      const containsSearchText = (text: string) =>
        text && text.toUpperCase().includes(searchTextInUpperCase);
      let filteredApplications = state.myApplicationsPEV.value;

      if (!state.filters.displayInactiveApplications) {
        filteredApplications = filteredApplications.filter((application) => action.payload.activeApplicationStatuses.includes(application.status));
      }
      if (searchTextInUpperCase) {
        filteredApplications = filteredApplications.filter((application) => (
          containsSearchText(application.name)
          || containsSearchText(application.description)
          || application.owner.some(containsSearchText)
        ));
      }
      filteredApplications = filterApplication(filteredApplications, state.filters.teamTypes, "teamType");
      filteredApplications = filterApplication(filteredApplications, state.filters.countries, "country");
      filteredApplications = filterApplication(filteredApplications, state.filters.businessFunctions, "businessFunction");
      filteredApplications = filterApplication(filteredApplications, state.filters.statuses, "status");
      filteredApplications = filterApplication(filteredApplications, state.filters.categories, "categories");
      state.filteredMyApplications = filteredApplications;
    },
  },
  extraReducers: {
    [fetchMyApplications.pending.type]: (state) => {
      state.myApplicationsPEV = PEV(state.myApplicationsPEV.value, "Fetching my applications.");
    },
    [fetchMyApplications.fulfilled.type]: (state, action: PayloadAction<ApplicationBasicInfo[]>) => {
      state.myApplicationsPEV = PEV(action.payload);
    },
    [fetchMyApplications.rejected.type]: (state, action: PayloadAction<NeoError>) => {
      state.myApplicationsPEV = PEV([], null, action.payload.getErrorInfo());
    },
  },
});

export const myAppsAndServicesPageReducer = myAppsAndServicesPageSlice.reducer;

export const MyAppsAndServicesPageActions = {
  fetchMyApplications,
  applyFilter: (filters: Partial<IMyApplicationFilters>, activeApplicationStatuses: Array<ApplicationBasicInfo["status"]>) =>
    myAppsAndServicesPageSlice.actions.applyFilters({ filters, activeApplicationStatuses }),
  resetFilters: (activeApplicationStatuses: Array<ApplicationBasicInfo["status"]>) => {
    const { searchText, ...filtersWithoutSearchText } = initialState.filters;
    return myAppsAndServicesPageSlice.actions.applyFilters({ filters: filtersWithoutSearchText, activeApplicationStatuses });
  },
  applySearch: (searchText: IMyApplicationFilters["searchText"], activeApplicationStatuses: Array<ApplicationBasicInfo["status"]>) =>
    myAppsAndServicesPageSlice.actions.applyFilters({ filters: { searchText }, activeApplicationStatuses }),
};
